import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { useNotify } from '@hooks/useNotify.ts';
import type { Subtask } from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api';
import {
    clearTaskCsvImportState,
    setTaskCsvImportErroneous,
    setTaskCsvImportFailed,
    setUploadedTaskCsvFilename,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/model/creatorElementsSlice';
import {
    buildCsvIssuesFile,
    buildFatalCsvIssueFile,
    createCsvBlob,
    CSV_DEFAULT_DELIMITER,
} from '@shared/csv/use-caret-utils';
import {
    TASK_CSV_HEADER,
    TASK_CSV_IMPORT_STATUS_FAILED,
    TASK_CSV_IMPORT_STATUS_PARTIAL,
    TASK_CSV_IMPORT_STATUS_SUCCESS,
    TASK_CSV_TEMPLATE,
} from '@shared/csv/use-task-utils/constants';
import { mergeSubtasksFromCsvImport } from '@shared/csv/use-task-utils/mergeTaskCsvImport.ts';
import { createTaskCsvHandler, taskCsvDefinition } from '@shared/csv/use-task-utils/taskCsvHandler';
import { downloadBlob } from '@utils/download';
import { showToast, TOAST_TYPE } from '@utils/toast.tsx';

export interface TaskCsvImportResult {
    status:
        | typeof TASK_CSV_IMPORT_STATUS_SUCCESS
        | typeof TASK_CSV_IMPORT_STATUS_PARTIAL
        | typeof TASK_CSV_IMPORT_STATUS_FAILED;
    importedCount: number;
    rejectedCount: number;
    subtasks?: Subtask[];
}

export const useTaskCsv = (subtasks: Subtask[] = [], descriptionMaxLength?: number, guidelineMaxLength?: number) => {
    const dispatch = useAppDispatch();
    const notify = useNotify();

    const taskCsvIssuesFileContent = useAppSelector(
        (state) => state.adminCenterCreatorElements.taskCsvIssuesFileContent,
    );

    const importTaskCsvFile = useCallback(
        async (file: File): Promise<TaskCsvImportResult> => {
            void dispatch(clearTaskCsvImportState());
            void dispatch(setUploadedTaskCsvFilename(file.name));

            const taskCsvHandler = createTaskCsvHandler({
                descriptionMaxLength,
                guidelineMaxLength,
            });

            const result = await taskCsvHandler.readFromFileWithValidation(file, subtasks);

            if (result.fatalError) {
                const issuesFileContent = buildFatalCsvIssueFile(
                    CSV_DEFAULT_DELIMITER,
                    TASK_CSV_HEADER,
                    2,
                    result.fatalError,
                );

                void dispatch(
                    setTaskCsvImportFailed({
                        issuesFileContent,
                    }),
                );

                return {
                    status: TASK_CSV_IMPORT_STATUS_FAILED,
                    importedCount: 0,
                    rejectedCount: 0,
                };
            }

            if (result.rowIssues.length > 0) {
                const issuesFileContent = buildCsvIssuesFile(CSV_DEFAULT_DELIMITER, TASK_CSV_HEADER, result.rowIssues);

                if (result.items.length > 0) {
                    void dispatch(
                        setTaskCsvImportErroneous({
                            issuesFileContent,
                            rejectedCount: result.rowIssues.length,
                            importedCount: result.items.length,
                        }),
                    );

                    return {
                        status: TASK_CSV_IMPORT_STATUS_PARTIAL,
                        importedCount: result.items.length,
                        rejectedCount: result.rowIssues.length,
                        subtasks: mergeSubtasksFromCsvImport(subtasks, result.items),
                    };
                }

                void dispatch(
                    setTaskCsvImportFailed({
                        issuesFileContent,
                    }),
                );

                return {
                    status: TASK_CSV_IMPORT_STATUS_FAILED,
                    importedCount: 0,
                    rejectedCount: result.rowIssues.length,
                };
            }

            if (result.items.length === 0) {
                void dispatch(setTaskCsvImportFailed());

                return {
                    status: TASK_CSV_IMPORT_STATUS_FAILED,
                    importedCount: 0,
                    rejectedCount: 0,
                };
            }

            showToast(notify, file.name, `${result.items.length} records uploaded successfully`, TOAST_TYPE.SUCCESS);

            return {
                status: TASK_CSV_IMPORT_STATUS_SUCCESS,
                importedCount: result.items.length,
                rejectedCount: 0,
                subtasks: mergeSubtasksFromCsvImport(subtasks, result.items),
            };
        },
        [dispatch, notify, subtasks, descriptionMaxLength, guidelineMaxLength],
    );

    const exportTaskCsvTemplate = useCallback(() => {
        downloadBlob(createCsvBlob(`${TASK_CSV_TEMPLATE}\n`), 'task-template.csv');
    }, []);

    const downloadTaskCsvIssues = useCallback(() => {
        if (!taskCsvIssuesFileContent) return;

        downloadBlob(
            createCsvBlob(taskCsvIssuesFileContent),
            taskCsvDefinition.issuesFileName ?? 'task-import-errors.csv',
        );
    }, [taskCsvIssuesFileContent]);

    return {
        importTaskCsvFile,
        exportTaskCsvTemplate,
        downloadTaskCsvIssues,
    };
};
